import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Badge,
    Box,
    Alert,
    Text,
    Tabs,
    Button,
    Modal,
    Group,
} from '@mantine/core';
import { ScimGroup } from '../../types/Groups';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityDetailDialog, GenericEntityUpsertDialog } from '../dialogs';
import { type TableColumn, DisplayNameCell } from '../common';
import { DynamicTableView } from '../common/DynamicTableView';
import { useLegacyConversion } from './hooks/useLegacyConversion';
import { useBulkOperations } from './hooks/useBulkOperations';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { LegacyBulkTab } from './tabs/LegacyBulkTab';
import { LegacyConverterTab } from './tabs/LegacyConverterTab';
import { ITEMS_PER_PAGE } from './types/GroupsManagementTypes';
import { pushHashRoute, replaceHashRoute } from '../../utils/hashNavigationUtils';
import { createGroupsManagementTableAdapter } from '../common/tableAdapters';
import { UnifiedEntityScreen } from '../entities/UnifiedEntityScreen';
import { buildEntityScreenHash } from '../../utils/entityScreenRouteUtils';
import { useViewModeSafe } from '../../contexts/ViewModeContext';
import { MODAL_SIZES } from '../../theme/mantineTheme';

interface GroupsManagementProps {
    selectedEntityId?: string | null;
    selectedEntityViewMode?: 'view' | 'edit' | 'create' | null;
    deepLinkEntityType?: string | null;
    authResolved?: boolean;
    /** When this changes (e.g. on-behalf-of toggled), the table refreshes. */
    /** When this changes (e.g. on-behalf-of toggled or UPN set/cleared), the table refreshes. */
    onBehalfOfKey?: string;
    onUnsavedStateChange?: (state: { active: boolean; hasUnsavedChanges: boolean; mode: 'create' | 'edit'; entityType: string }) => void;
}


/**
 * Groups Management component - Refactored to use custom hooks
 */
const GroupsManagement: React.FC<GroupsManagementProps> = ({
    selectedEntityId,
    selectedEntityViewMode,
    deepLinkEntityType,
    authResolved = false,
    onBehalfOfKey,
    onUnsavedStateChange
}) => {
    // ========== SERVICE AND SCHEMA SETUP ==========

    const { isStandard } = useViewModeSafe();
    const entityServiceRef = useRef(entityService);

    // ========== UI-SPECIFIC STATE ==========

    const [selectedGroup, setSelectedGroup] = useState<any>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState<any>(null);
    const [editingTargetEntitlement, setEditingTargetEntitlement] = useState<any>(undefined);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [cloneGroupOpen, setCloneGroupOpen] = useState(false);
    const [cloneGroupEntity, setCloneGroupEntity] = useState<any>(null);
    const [activeFilterTab, setActiveFilterTab] = useState<number>(0);
    const activeFilterTabRef = useRef(activeFilterTab);
    useEffect(() => {
        activeFilterTabRef.current = activeFilterTab;
    }, [activeFilterTab]);
    const [groupsRefreshSignal, setGroupsRefreshSignal] = useState(0);
    const newUiOpen = !!selectedEntityId
        && (selectedEntityViewMode === 'view' || selectedEntityViewMode === 'edit');
    const [mainViewTab, setMainViewTab] = useState<number>(0);
    const [legacySuccess, setLegacySuccess] = useState<string | null>(null);
    const [legacyError, setLegacyError] = useState<string | null>(null);

    const editPencilClickedRef = useRef(false);
    /** Set when closing edit dialog so the "open edit from URL" effect skips one run and doesn't re-open. */
    const editDialogClosingRef = useRef(false);

    // ========== CUSTOM HOOKS ==========

    const legacyConversionHook = useLegacyConversion(entityServiceRef);
    const {
        allowedTableAttributeIds,
        canEditRow,
        isDisjointed
    } = usePerEntityTableEntitlement({
        entityType: 'group',
        authResolved,
        onBehalfOfKey,
        onlyWhenEntityTypeSet: false
    });
    const allowedTableAttributeIdsRef = useRef(allowedTableAttributeIds);
    useEffect(() => { allowedTableAttributeIdsRef.current = allowedTableAttributeIds; }, [allowedTableAttributeIds]);

    const bulkOperationsHook = useBulkOperations({
        entityServiceRef,
        parseLegacyFilterWithErrorHandling: legacyConversionHook.parseLegacyFilterWithErrorHandling,
        processFilterPipelineWithDeps: legacyConversionHook.processFilterPipelineWithDeps,
        loadResponsibilityCenterCache: legacyConversionHook.loadResponsibilityCenterCache,
        batchResolveLegacyRcIds: legacyConversionHook.batchResolveLegacyRcIds,
        getRCResolutionDeps: legacyConversionHook.getRCResolutionDeps,
        cleanSetDisplayName: legacyConversionHook.cleanSetDisplayName,
        extractAttributesFromFilter: legacyConversionHook.extractAttributesFromFilter,
        addUntranslatedRcIds: legacyConversionHook.addUntranslatedRcIds,
        rcNotFoundCacheRef: legacyConversionHook.rcNotFoundCacheRef,
        setLegacySuccess,
        setLegacyError
    });

    // Table view error state (for deep link and dialogs)
    const [tableError, setTableError] = useState<string | null>(null);


    const {
        bulkGroups,
        selectedBulkGroups,
        bulkGroupsPage, setBulkGroupsPage,
        bulkGroupsRowsPerPage, setBulkGroupsRowsPerPage,
        bulkGroupsTotalResults,
        loadingBulkGroups,
        processingBulkGroups,
        processingStats,
        conversionStats,
        bulkGroupStatuses,
        editingConvertedFilter, setEditingConvertedFilter,
        editedConvertedFilters, setEditedConvertedFilters,
        bulkSubTab, setBulkSubTab,
        excludeManaged, setExcludeManaged,
        excludeHasFilter, setExcludeHasFilter,
        excludeJobCodeMemberOf, setExcludeJobCodeMemberOf,
        bulkCustomFilter, setBulkCustomFilter,
        effectiveFilter,
        loadBulkGroups,
        handleSelectBulkGroup,
        handleSelectAllBulkGroups,
        handleProcessBulkGroups,
        handleCancelBulkProcessing,
        handleReloadBulkGroups,
        handleUpdateFilters,
        handleRefreshRow,
        handleEditedFilterBlur
    } = bulkOperationsHook;

    const {
        untranslatedRcIds,
        showClearRcIdsDialog, setShowClearRcIdsDialog,
        untranslatedJobCodeGuids,
        showClearJobCodeGuidsDialog, setShowClearJobCodeGuidsDialog,
        clearUntranslatedRcIds,
        clearUntranslatedJobCodeGuids
    } = legacyConversionHook;

    // ========== DEEP LINKING ==========
    // Treat invalid IDs (undefined, empty, or literal "undefined") as no deep-link so we never fetch group/undefined
    const validGroupId = (id: string | null | undefined): id is string =>
        typeof id === 'string' && id.trim() !== '' && id !== 'undefined';

    useEffect(() => {
        if (!validGroupId(selectedEntityId)) {
            setEditDialogOpen(false);
            setEditingGroup(null);
            setDetailDialogOpen(false);
            setSelectedGroup(null);
            editDialogClosingRef.current = false;
            if (selectedEntityId !== undefined && selectedEntityId !== null) {
                replaceHashRoute('#groups');
            }
            return;
        }
        if (newUiOpen && authResolved) {
            const stub = { Id: selectedEntityId };
            if (selectedEntityViewMode === 'edit') {
                if (!editingGroup || (editingGroup.Id ?? editingGroup.id) !== selectedEntityId) {
                    setEditingGroup(stub);
                }
            } else if (!selectedGroup || (selectedGroup.Id ?? selectedGroup.id) !== selectedEntityId) {
                setSelectedGroup(stub);
            }
        }
    }, [selectedEntityId, selectedEntityViewMode, authResolved, newUiOpen, editingGroup, selectedGroup]);

    useEffect(() => {
        if (!newUiOpen) return;
        setDetailDialogOpen(false);
        setEditDialogOpen(false);
    }, [newUiOpen]);

    // ========== URL HASH HANDLING ==========
    // DynamicTableView manages its own search state; deep link to entity is handled above.


    // ========== GROUP SELECTION HANDLERS ==========

    const getCurrentHashSearch = useCallback((): string => {
        try {
            const h = typeof window !== 'undefined' ? (window.location.hash ?? '') : '';
            const idx = h.indexOf('?');
            return idx >= 0 ? h.slice(idx) : '';
        } catch {
            return '';
        }
    }, []);

    const buildNewUiHash = useCallback((mode: 'view' | 'edit', entityId: string) => {
        const currentSearch = getCurrentHashSearch();
        const next = buildEntityScreenHash('#groups', mode, entityId);
        return currentSearch && !next.includes('?') ? `${next}${currentSearch}` : next;
    }, [getCurrentHashSearch]);

    const handleGroupSelect = useCallback(async (group: ScimGroup) => {
        if (editPencilClickedRef.current) return;
        const id = group?.Id;
        if (!validGroupId(id)) return;
        try {
            setSelectedGroup(group);
            const next = buildNewUiHash('view', id);
            pushHashRoute(next);
        } catch (err) {
            console.error('Error loading group:', err);
            setTableError(err instanceof Error ? err.message : 'Failed to load group');
        }
    }, [buildNewUiHash]);

    const handleEditGroup = useCallback((group: any, targetEntitlement?: any) => {
        editPencilClickedRef.current = true;
        setDetailDialogOpen(false);
        setSelectedGroup(null);
        const entityId = group?.Id || group?.id;
        if (entityId) {
            const next = buildNewUiHash('edit', entityId);
            pushHashRoute(next);
        }
        setEditingGroup(group);
        setEditingTargetEntitlement(targetEntitlement);
        setTimeout(() => {
            editPencilClickedRef.current = false;
        }, 0);
    }, [buildNewUiHash]);

    const handleCloseDetailDialog = useCallback(() => {
        setDetailDialogOpen(false);
        setSelectedGroup(null);
        const currentSearch = getCurrentHashSearch();
        const baseHash = '#groups';
        const next = currentSearch && !baseHash.includes('?') ? `${baseHash}${currentSearch}` : baseHash;
        // Deterministically leave /view/{id} so URL and table state stay in sync.
        replaceHashRoute(next);
    }, [getCurrentHashSearch]);

    const handleCloseEditDialog = useCallback(() => {
        editDialogClosingRef.current = true;
        setEditDialogOpen(false);
        setEditingGroup(null);
        setEditingTargetEntitlement(undefined);
        const currentSearch = getCurrentHashSearch();
        const baseHash = '#groups';
        const next = currentSearch && !baseHash.includes('?') ? `${baseHash}${currentSearch}` : baseHash;
        replaceHashRoute(next);
    }, [getCurrentHashSearch]);

    const handleSaveEdit = useCallback(() => {
        editDialogClosingRef.current = true;
        setEditDialogOpen(false);
        setEditingGroup(null);
        setEditingTargetEntitlement(undefined);
        setGroupsRefreshSignal((s) => s + 1);
    }, []);

    const handleFilterTabChange = useCallback((_event: React.SyntheticEvent, newValue: number) => {
        if (newValue !== activeFilterTab) {
            setActiveFilterTab(newValue);
            setGroupsRefreshSignal((s) => s + 1);
        }
    }, [activeFilterTab]);

    // Handle viewing bulk group details — pass stub so EntityDetailDialog fetches with tier3-driven attributes= (no full group/Members).
    const handleViewBulkGroupDetail = useCallback((groupId: string) => {
        if (!validGroupId(groupId)) return;
        setSelectedGroup({ Id: groupId });
        setDetailDialogOpen(true);
        const currentSearch = getCurrentHashSearch();
        const baseHash = `#groups/view/${groupId}`;
        const next = currentSearch && !baseHash.includes('?') ? `${baseHash}${currentSearch}` : baseHash;
        pushHashRoute(next);
    }, [getCurrentHashSearch]);


    // ========== GROUPS TABLE (DynamicTableView) ==========

    const generateColumns = useCallback((): TableColumn<ScimGroup>[] => {
        const allowedSet = allowedTableAttributeIds == null ? undefined : new Set(allowedTableAttributeIds.map((a) => a.toLowerCase()));

        const MANAGED_TYPE_COLORS: Record<string, string> = { manual: 'green', enumerated: 'blue', external: 'gray' };
        const TYPE_COLORS: Record<string, string> = { security: 'red', distribution: 'teal' };

        let cols: TableColumn<ScimGroup>[];

        if (activeFilterTab === 1) {
            cols = [
                {
                    key: 'DisplayName', label: 'Display Name', title: 'DisplayName', sortable: true,
                    render: (group, index) => <DisplayNameCell displayName={group.DisplayName} id={group.Id} index={index} startIndex={0} />,
                },
                {
                    key: 'ManagedType', label: 'Mode', title: 'ManagedType', sortable: true, width: '110px',
                    render: (group) => {
                        const mt = (group.ManagedType || '').toString().trim();
                        if (!mt) return <Text size="sm" c="dimmed">—</Text>;
                        const color = MANAGED_TYPE_COLORS[mt.toLowerCase()] || 'gray';
                        return <Badge variant="light" color={color} size="sm">{mt}</Badge>;
                    },
                },
                {
                    key: 'Type', label: 'Type', title: 'Type', sortable: true, width: '120px',
                    render: (group) => {
                        const t = (group.Type || '').toString().trim();
                        if (!t) return <Text size="sm" c="dimmed">—</Text>;
                        const color = TYPE_COLORS[t.toLowerCase()] || 'gray';
                        return <Badge variant="light" color={color} size="sm">{t}</Badge>;
                    },
                },
                {
                    key: 'LegacyFilter', label: 'Legacy Filter', title: 'LegacyFilter', sortable: true,
                    render: (group) => <Text size="xs" ff="monospace">{(group as any).LegacyFilter || '—'}</Text>,
                },
                { key: 'FilterScope', label: 'Filter Scope', title: 'FilterScope', sortable: true, render: (group) => (group as any).FilterScope || '—' },
                {
                    key: 'Filter', label: 'Filter', title: 'Filter', sortable: true,
                    render: (group) => <Text size="xs" ff="monospace">{(group as any).Filter || '—'}</Text>,
                },
                { key: 'Id', label: 'Id', title: 'Id', sortable: true, render: (group) => group.Id || '—' },
            ];
        } else {
            cols = [
                {
                    key: 'DisplayName', label: 'Display Name', title: 'DisplayName', sortable: true,
                    render: (group, index) => <DisplayNameCell displayName={group.DisplayName} id={group.Id} index={index} startIndex={0} />,
                },
                {
                    key: 'ManagedType', label: 'Mode', title: 'ManagedType', sortable: true, width: '110px',
                    render: (group) => {
                        const mt = (group.ManagedType || '').toString().trim();
                        if (!mt) return <Text size="sm" c="dimmed">—</Text>;
                        const color = MANAGED_TYPE_COLORS[mt.toLowerCase()] || 'gray';
                        return <Badge variant="light" color={color} size="sm">{mt}</Badge>;
                    },
                },
                {
                    key: 'Type', label: 'Type', title: 'Type', sortable: true, width: '120px',
                    render: (group) => {
                        const t = (group.Type || '').toString().trim();
                        if (!t) return <Text size="sm" c="dimmed">—</Text>;
                        const color = TYPE_COLORS[t.toLowerCase()] || 'gray';
                        return <Badge variant="light" color={color} size="sm">{t}</Badge>;
                    },
                },
                {
                    key: 'Description', label: 'Description', title: 'Description', sortable: true,
                    render: (group) => {
                        const desc = group.Description || '';
                        if (!desc) return '';
                        return desc.length > 80 ? `${desc.substring(0, 80)}...` : desc;
                    },
                },
                { key: 'Email', label: 'Email', title: 'Email', sortable: true, render: (group) => group.Email || '' },
                ...(!isStandard ? [
                    { key: 'Scope', label: 'Scope', title: 'Scope', sortable: true, width: '100px', render: (group: ScimGroup) => (group.Scope || '') } as TableColumn<ScimGroup>,
                    { key: 'Id', label: 'Id', title: 'Id', sortable: true, render: (group: ScimGroup) => group.Id || '—' } as TableColumn<ScimGroup>,
                ] : []),
            ];
        }

        if (allowedSet !== undefined) {
            const alwaysShow = new Set(['id', 'displayname']);
            cols = cols.filter((c) => alwaysShow.has(c.key.toLowerCase()) || allowedSet.has(c.key.toLowerCase()));
        }
        return cols;
    }, [activeFilterTab, allowedTableAttributeIds, isStandard]);

    const groupsTableAdapter = useMemo(
        () =>
            createGroupsManagementTableAdapter({
                authResolved,
                entityServiceRef,
                activeFilterTabRef,
                allowedTableAttributeIdsRef,
                itemsPerPage: ITEMS_PER_PAGE,
                getColumns: generateColumns
            }),
        [authResolved, generateColumns]
    );

    // ========== RENDER ==========

    return (
        <Box p="md">
            {tableError && (
                <Alert color="red" mb="md" withCloseButton onClose={() => setTableError(null)}>
                    {tableError}
                </Alert>
            )}

            {/* Main View Tabs -- Legacy tabs only shown in admin mode */}
            {!isStandard && (
                <Tabs value={String(mainViewTab)} onChange={(value) => {
                    const newValue = Number(value);
                    setMainViewTab(newValue);
                    if (newValue !== 0) {
                        setDetailDialogOpen(false);
                        setSelectedGroup(null);
                        replaceHashRoute('#groups');
                    }
                }} mb="md">
                    <Tabs.List>
                        <Tabs.Tab value="0">Table View</Tabs.Tab>
                        <Tabs.Tab value="1">Legacy Converter</Tabs.Tab>
                        <Tabs.Tab value="2">Legacy Bulk</Tabs.Tab>
                    </Tabs.List>
                </Tabs>
            )}

            {/* Table View - DynamicTableView (same table view as localities/dynamic entities) */}
            {mainViewTab === 0 && !newUiOpen && (
                <DynamicTableView
                    entityType={groupsTableAdapter.entityType}
                    selectedEntityId={selectedEntityId}
                    deepLinkEntityType={deepLinkEntityType}
                    authResolved={authResolved}
                    loadData={groupsTableAdapter.loadData}
                    generateColumns={() => groupsTableAdapter.getColumns()}
                    onEntitySelect={selectedEntityViewMode === 'edit' ? undefined : handleGroupSelect}
                    onEditRowClick={(group) => handleEditGroup(group, undefined)}
                    onBehalfOfKey={onBehalfOfKey}
                    refreshSignal={groupsRefreshSignal}
                    deleteEntityById={groupsTableAdapter.deleteById}
                    customHeaderContent={!isStandard ? (
                        <Tabs value={String(activeFilterTab)} onChange={(value) => handleFilterTabChange(null as any, Number(value))} mb="md">
                            <Tabs.List>
                                <Tabs.Tab value="0">All</Tabs.Tab>
                                <Tabs.Tab value="1">Legacy Filter</Tabs.Tab>
                            </Tabs.List>
                        </Tabs>
                    ) : undefined}
                />
            )}

            {/* Legacy Converter Tab (admin only) */}
            {!isStandard && mainViewTab === 1 && (
                <LegacyConverterTab
                    entityServiceRef={entityServiceRef}
                    legacySuccess={legacySuccess}
                    legacyError={legacyError}
                    setLegacySuccess={setLegacySuccess}
                    setLegacyError={setLegacyError}
                />
            )}

            {/* Legacy Bulk Tab (admin only) */}
            {!isStandard && mainViewTab === 2 && (
                <LegacyBulkTab
                    bulkGroups={bulkGroups}
                    selectedBulkGroups={selectedBulkGroups}
                    bulkGroupsPage={bulkGroupsPage}
                    bulkGroupsRowsPerPage={bulkGroupsRowsPerPage}
                    bulkGroupsTotalResults={bulkGroupsTotalResults}
                    loadingBulkGroups={loadingBulkGroups}
                    processingBulkGroups={processingBulkGroups}
                    processingStats={processingStats}
                    conversionStats={conversionStats}
                    bulkGroupStatuses={bulkGroupStatuses}
                    editingConvertedFilter={editingConvertedFilter}
                    editedConvertedFilters={editedConvertedFilters}
                    bulkSubTab={bulkSubTab}
                    excludeManaged={excludeManaged}
                    excludeHasFilter={excludeHasFilter}
                    excludeJobCodeMemberOf={excludeJobCodeMemberOf}
                    bulkCustomFilter={bulkCustomFilter}
                    effectiveFilter={effectiveFilter}
                    legacySuccess={legacySuccess}
                    legacyError={legacyError}
                    setBulkGroupsPage={setBulkGroupsPage}
                    setBulkGroupsRowsPerPage={setBulkGroupsRowsPerPage}
                    setEditingConvertedFilter={setEditingConvertedFilter}
                    setEditedConvertedFilters={setEditedConvertedFilters}
                    setBulkSubTab={setBulkSubTab}
                    setExcludeManaged={setExcludeManaged}
                    setExcludeHasFilter={setExcludeHasFilter}
                    setExcludeJobCodeMemberOf={setExcludeJobCodeMemberOf}
                    setBulkCustomFilter={setBulkCustomFilter}
                    setLegacySuccess={setLegacySuccess}
                    setLegacyError={setLegacyError}
                    loadBulkGroups={loadBulkGroups}
                    handleSelectBulkGroup={handleSelectBulkGroup}
                    handleSelectAllBulkGroups={handleSelectAllBulkGroups}
                    handleProcessBulkGroups={handleProcessBulkGroups}
                    handleCancelBulkProcessing={handleCancelBulkProcessing}
                    handleReloadBulkGroups={handleReloadBulkGroups}
                    handleUpdateFilters={handleUpdateFilters}
                    handleRefreshRow={handleRefreshRow}
                    handleEditedFilterBlur={handleEditedFilterBlur}
                    onViewDetails={handleViewBulkGroupDetail}
                />
            )}

            {/* Dialogs */}
            {selectedGroup && (
                <EntityDetailDialog
                    open={detailDialogOpen && selectedEntityViewMode !== 'edit' && !newUiOpen}
                    onClose={handleCloseDetailDialog}
                    entity={selectedGroup}
                    entityType="Group"
                    selectedEntityId={selectedEntityId ?? undefined}
                    onEdit={(group, targetEntitlement) => {
                        handleEditGroup(group, targetEntitlement);
                    }}
                    onOpenNewUi={(entity) => {
                        const entityId = entity?.Id || entity?.id;
                        if (!entityId) return;
                        pushHashRoute(buildNewUiHash('view', entityId));
                        setDetailDialogOpen(false);
                    }}
                    onClone={(entity) => {
                        setDetailDialogOpen(false);
                        setCloneGroupEntity(entity);
                        setCloneGroupOpen(true);
                    }}
                    onDelete={async (entity) => {
                        const entityId = entity.Id || entity.id;
                        await entityServiceRef.current!.deleteEntity('Group', entityId);
                        setDetailDialogOpen(false);
                        setSelectedGroup(null);
                        pushHashRoute('#groups');
                        setGroupsRefreshSignal((s) => s + 1);
                    }}
                />
            )}

            {/* Edit Dialog for Group */}
            {editingGroup && (
                <GenericEntityUpsertDialog
                    key={`edit-group-${editingGroup.Id || editingGroup.id}`}
                    open={editDialogOpen && !newUiOpen}
                    onClose={handleCloseEditDialog}
                    onSuccess={handleSaveEdit}
                    entityType="Group"
                    entityId={editingGroup.Id || editingGroup.id}
                    existingEntity={editingGroup}
                    targetEntitlement={editingTargetEntitlement}
                    onOpenNewUi={() => {
                        const entityId = editingGroup?.Id || editingGroup?.id;
                        if (!entityId) return;
                        pushHashRoute(buildNewUiHash('edit', entityId));
                        setEditDialogOpen(false);
                    }}
                />
            )}

            <UnifiedEntityScreen
                open={newUiOpen}
                entityType="Group"
                entityId={selectedEntityId ?? ''}
                mode={selectedEntityViewMode === 'edit' ? 'edit' : 'view'}
                existingEntity={editingGroup ?? selectedGroup}
                targetEntitlement={editingTargetEntitlement}
                onClose={() => {
                    const currentSearch = getCurrentHashSearch();
                    const baseHash = '#groups';
                    const next = currentSearch && !baseHash.includes('?') ? `${baseHash}${currentSearch}` : baseHash;
                    replaceHashRoute(next);
                }}
                onModeChange={(mode) => {
                    if (!selectedEntityId) return;
                    replaceHashRoute(buildNewUiHash(mode, selectedEntityId));
                }}
                onSuccess={() => {
                    const currentSearch = getCurrentHashSearch();
                    const baseHash = '#groups';
                    const next = currentSearch && !baseHash.includes('?') ? `${baseHash}${currentSearch}` : baseHash;
                    replaceHashRoute(next);
                    setGroupsRefreshSignal((s) => s + 1);
                }}
                onUnsavedStateChange={onUnsavedStateChange}
            />

            {/* Clone Dialog for Group */}
            {cloneGroupOpen && cloneGroupEntity && (
                <GenericEntityUpsertDialog
                    key={`clone-group-${cloneGroupEntity.Id || cloneGroupEntity.id || 'new'}`}
                    open={cloneGroupOpen}
                    onClose={() => { setCloneGroupOpen(false); setCloneGroupEntity(null); }}
                    onSuccess={() => { setCloneGroupOpen(false); setCloneGroupEntity(null); setGroupsRefreshSignal((s) => s + 1); }}
                    entityType="Group"
                    existingEntity={{ ...cloneGroupEntity, Id: undefined, id: undefined, DisplayName: cloneGroupEntity.DisplayName ? `${cloneGroupEntity.DisplayName} (Copy)` : undefined }}
                />
            )}

            {/* Untranslated RC IDs Dialog */}
            <Modal opened={showClearRcIdsDialog} onClose={() => setShowClearRcIdsDialog(false)} title="Clear Untranslated RC IDs" size={MODAL_SIZES.confirm}>
                <Text mb="md">
                    This will clear {untranslatedRcIds.size} untranslated RC IDs from the cache.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setShowClearRcIdsDialog(false)}>Cancel</Button>
                    <Button color="red" onClick={clearUntranslatedRcIds}>
                        Clear
                    </Button>
                </Group>
            </Modal>

            {/* Untranslated JobCode GUIDs Dialog */}
            <Modal opened={showClearJobCodeGuidsDialog} onClose={() => setShowClearJobCodeGuidsDialog(false)} title="Clear Untranslated JobCode GUIDs" size={MODAL_SIZES.confirm}>
                <Text mb="md">
                    This will clear {untranslatedJobCodeGuids.size} untranslated JobCode GUIDs from the cache.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setShowClearJobCodeGuidsDialog(false)}>Cancel</Button>
                    <Button color="red" onClick={clearUntranslatedJobCodeGuids}>
                        Clear
                    </Button>
                </Group>
            </Modal>

        </Box>
    );
};

export default GroupsManagement;

